export const createEmptyTaskRichTextDocument = () => {
  return {
    type: 'doc',
    content: [{
      type: 'paragraph',
    }],
  }
}

export const stringifyTaskRichTextDocument = (documentNode) => {
  return JSON.stringify(documentNode)
}

const sanitizeTiptapMarks = (marks) => {
  if (!Array.isArray(marks)) {
    return undefined
  }

  const sanitizedMarks = marks
    .map((mark) => {
      if (!mark || typeof mark !== 'object' || Array.isArray(mark)) {
        return null
      }

      const type = typeof mark.type === 'string' ? mark.type : ''

      if (type === '') {
        return null
      }

      const sanitizedMark = {
        type,
      }

      if (mark.attrs && typeof mark.attrs === 'object' && !Array.isArray(mark.attrs)) {
        sanitizedMark.attrs = { ...mark.attrs }
      }

      return sanitizedMark
    })
    .filter((mark) => mark !== null)

  if (sanitizedMarks.length === 0) {
    return undefined
  }

  return sanitizedMarks
}

const sanitizeTiptapNode = (node) => {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return null
  }

  const nodeType = typeof node.type === 'string' ? node.type : ''

  if (nodeType === '') {
    return null
  }

  if (nodeType === 'text') {
    if (node.text === null || node.text === undefined) {
      return null
    }

    const sanitizedTextNode = {
      type: 'text',
      text: typeof node.text === 'string' ? node.text : String(node.text),
    }
    const sanitizedMarks = sanitizeTiptapMarks(node.marks)

    if (sanitizedMarks) {
      sanitizedTextNode.marks = sanitizedMarks
    }

    return sanitizedTextNode
  }

  const sanitizedNode = {
    type: nodeType,
  }

  if (node.attrs && typeof node.attrs === 'object' && !Array.isArray(node.attrs)) {
    sanitizedNode.attrs = { ...node.attrs }
  }

  if (Array.isArray(node.content)) {
    sanitizedNode.content = node.content
      .map((childNode) => sanitizeTiptapNode(childNode))
      .filter((childNode) => childNode !== null)
  }

  return sanitizedNode
}

const createTaskRichTextDocumentFromText = (textContent) => {
  if (typeof textContent !== 'string' || textContent.trim() === '') {
    return createEmptyTaskRichTextDocument()
  }

  const paragraphs = textContent
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => ({
      type: 'paragraph',
      content: [{
        type: 'text',
        text: line,
      }],
    }))

  if (paragraphs.length === 0) {
    return createEmptyTaskRichTextDocument()
  }

  return {
    type: 'doc',
    content: paragraphs,
  }
}

const sanitizeTaskRichTextDocument = (documentNode) => {
  const normalizedContent = Array.isArray(documentNode?.content) ? documentNode.content : []
  const sanitizedContent = normalizedContent
    .map((node) => sanitizeTiptapNode(node))
    .filter((node) => node !== null)

  if (sanitizedContent.length === 0) {
    sanitizedContent.push({
      type: 'paragraph',
    })
  }

  return {
    type: 'doc',
    content: sanitizedContent,
  }
}

export const normalizeTaskRichTextDocument = (documentNode, fallbackTextContent = '') => {
  if (!documentNode || typeof documentNode !== 'object' || Array.isArray(documentNode)) {
    return createTaskRichTextDocumentFromText(fallbackTextContent)
  }

  if (documentNode.type !== 'doc') {
    return createTaskRichTextDocumentFromText(fallbackTextContent)
  }

  return sanitizeTaskRichTextDocument(documentNode)
}

export const parseTaskRichTextDescription = (description) => {
  if (typeof description !== 'string') {
    return createEmptyTaskRichTextDocument()
  }

  if (description.trim() === '') {
    return createEmptyTaskRichTextDocument()
  }

  try {
    const parsedDescription = JSON.parse(description)

    return normalizeTaskRichTextDocument(parsedDescription, description)
  } catch {
    return createTaskRichTextDocumentFromText(description)
  }
}

export const taskRichTextDocumentHasRenderableContent = (documentNode) => {
  if (!documentNode || typeof documentNode !== 'object' || Array.isArray(documentNode)) {
    return false
  }

  if (documentNode.type === 'text') {
    return typeof documentNode.text === 'string' && documentNode.text.trim() !== ''
  }

  if (documentNode.type === 'image') {
    return typeof documentNode.attrs?.src === 'string' && documentNode.attrs.src.trim() !== ''
  }

  if (!Array.isArray(documentNode.content)) {
    return false
  }

  return documentNode.content.some((childNode) => taskRichTextDocumentHasRenderableContent(childNode))
}

export const serializeTaskRichTextDocument = (documentNode) => {
  const normalizedDocument = normalizeTaskRichTextDocument(documentNode)

  if (!taskRichTextDocumentHasRenderableContent(normalizedDocument)) {
    return null
  }

  return stringifyTaskRichTextDocument(normalizedDocument)
}
